import { z } from 'zod'
import { toDynamicOptionDataSource } from '@omnea-app/packages/universal/core-data/formatDependencyDataType'

// What a reference can point at: the vocabulary an annotation may use. Where a kind resolves belongs
// to the reader, which does not exist yet.
export const REF_KINDS = [
  // Unscoped when named by id. A scope is a form type, for a block that depends on every template of
  // that type without naming one.
  'formTemplate',
  'customField',
  // The whole set of options, named by a system source for the built-in sets and a uuid for a custom
  // one — so resolution branches on shape, though the reference is the same either way.
  'coreDataSet',
  // Scoped by the set it belongs to: an option id alone does not say which table to look in.
  'coreDataOption',
  'conditionGroup',
  'autoFlaggedScoreCategory',
  // A row in the org's shared question library, not a question defined inside one form's config:
  // deleting one leaves every Form Template linking it with an entry that cannot be resolved.
  'organisationQuestion',
  'documentType',
  // A single choice on a custom field, not the field itself: deleting one orphans the answers that
  // picked it while the field survives.
  'customFieldChoice',
  'policy',
  'scorecardTemplate',
  'customAgent',
  // A row of `budget_approval_group`, not of `groups`: the two are separate entities, and a config
  // field holding one never holds the other.
  'budgetApprovalGroup',
  'subWorkflow',
  'workflow',
  'user',
  'group',
  // An extops procedure template id stored in dynamoDB, not a row of this database: nothing here resolves it, and
  // environment sync cannot match it. Recorded so the workflows binding a template can be listed.
  'procedureTemplate',
] as const

export type RefKind = (typeof REF_KINDS)[number]

/**
 * A core data set is named in the dynamic-option vocabulary — 'line-item-types', 'subsidiaries',
 * and 'department', the one that stays singular — while the fields holding one store whichever
 * spelling their own picker writes. A row spelt the other way joins with nothing, which a guard
 * reads as a match resolving to nothing rather than as an honest unknown. A custom set is a uuid
 * and passes through, as does any name the vocabulary does not recognise.
 */
const KINDS: Partial<Record<RefKind, { spellAs?: (value: string) => string; scopeKind?: RefKind }>> = {
  coreDataSet: { spellAs: toDynamicOptionDataSource },
  coreDataOption: { scopeKind: 'coreDataSet' },
}

export const canonicalIdentity = (kind: RefKind, identity: string): string => KINDS[kind]?.spellAs?.(identity) ?? identity

export const canonicalScope = (kind: RefKind, scope: string): string => {
  const scopeKind = KINDS[kind]?.scopeKind
  return scopeKind === undefined ? scope : canonicalIdentity(scopeKind, scope)
}

// What a reference can sit inside. Deliberately disjoint from RefKind: references between blocks of
// one workflow never leave the document, so they are not stored and `block` is not a kind.
//
// These nest — a question sits on a page, and a task, a block's run condition or a sub-workflow's trigger
// sits inside a block — which is why the walk records the whole trail rather than the innermost one.
export const SITE_KINDS = ['block', 'blockCondition', 'formRule', 'page', 'question', 'subWorkflowTrigger', 'task'] as const
export type SiteKind = (typeof SITE_KINDS)[number]

export type ReferenceMeta = {
  refKind?: RefKind
  scopeFrom?: string
  scope?: string
  siteKind?: SiteKind
  soleSiteKind?: SiteKind
  refEveryMember?: EveryMemberReference
  refsAt?: Record<string, RefDeclaration>
}

// Not zod's global registry: the OpenAPI generator publishes everything in that one.
export const referenceRegistry = z.registry<ReferenceMeta>()

// Clones first, as `.meta()` does, so declaring on a shared schema never touches its other uses.
const declareOn = <T extends z.ZodType>(schema: T, meta: ReferenceMeta): T => {
  const declared = schema.clone()
  referenceRegistry.add(declared, meta)
  return declared
}

export type DeclaredReference = { kind: RefKind; scopeFrom?: string }

export type RefDeclaration = RefKind | DeclaredReference

export const declaredReference = (declaration: RefDeclaration): DeclaredReference =>
  typeof declaration === 'string' ? { kind: declaration } : declaration

type FixedScope = { scope: string; scopeFrom?: never }
type ReadScope = { scopeFrom: string; scope?: never }

export type ScopeDeclaration = Pick<ReferenceMeta, 'scope' | 'scopeFrom'>

/**
 * The qualifier for a kind only unique within a partition. `scopeFrom` reads it from a field of the
 * document — the walk searches outward from the annotated field, nearest wins; `scope` states it
 * outright, for a field that only ever holds members of one set.
 */
type RefOpts = Partial<FixedScope> | Partial<ReadScope>

/**
 * Declares the field's value as a reference. Plain string, because some kinds identify by name
 * rather than by row id; for a uuid or an enum, use `refOn`.
 *
 * Do not chain `.openapi()` onto the result: it clones the schema into its own registry and the
 * annotation is lost, with no error and no row. Call it on the underlying schema first, or on a
 * wrapper such as `.optional()`, both of which keep the annotation reachable.
 */
export const ref = <K extends RefKind>(kind: K, opts?: RefOpts) => declareOn(z.string(), { refKind: kind, ...opts })

/** The same declaration for a field `ref()` cannot supply the base type for — an enum, or a uuid. */
export const refOn = <T extends z.ZodType, K extends RefKind>(schema: T, kind: K, opts?: RefOpts): T =>
  declareOn(schema, { refKind: kind, ...opts })

/**
 * The scope is required: a row with neither an identity nor a scope names nothing, and the table
 * refuses one, which would abort the config save it is recorded in.
 */
type EveryMemberReference = { kind: RefKind } & (FixedScope | ReadScope)

/**
 * Declares that carrying this field at all implies a dependency on every member of a scope, for the
 * cases where config stores no id: a bankAccounts question needs every bank-account custom field and
 * names none of them. Belongs on the discriminant, the field that says what this is.
 *
 * `scopeFrom` is for a set the document names rather than the schema — a dynamic-option question
 * depends on every option of whichever set its `dataSource` holds — and is read as it is for `ref`.
 */
export const refEveryMember = <T extends z.ZodLiteral<string>>(schema: T, reference: EveryMemberReference): T =>
  declareOn(schema, { refEveryMember: reference })

/**
 * Zod cannot patch a leaf of a composed schema in place, so a declaration about a library's field sits
 * outside the schema and names its position. Nothing at runtime checks that the position still exists,
 * so only a `refPathProblems` test notices a path the library has dropped; paths behave as
 * `walkReferences` reads them.
 */
export const refsAt = <T extends z.ZodType>(schema: T, refs: Record<string, RefDeclaration>): T => {
  const unfollowable = Object.keys(refs).find((path) => /[[\]{}*]/.test(path))
  if (unfollowable !== undefined)
    throw new Error(`refsAt: keys are plain dotted field paths, and the walk crosses arrays by itself: '${unfollowable}'`)
  return declareOn(schema, { refsAt: refs })
}

/**
 * Marks the field holding the id of the site references here sit in — a block, a question. On a
 * record's key schema, it makes each entry a site named by its key.
 *
 * Goes on the id rather than the object because object metadata does not survive `.extend()`,
 * `.merge()` or `.partial()`, and every question in form config is built from those.
 */
export const siteId = <T extends z.ZodType>(schema: T, kind: SiteKind): T => declareOn(schema, { siteKind: kind })

/**
 * Marks a field that is itself a site but stores no id, such as a block's run condition. There is
 * never more than one of its kind in the site that holds it, so it takes that site's id.
 */
export const soleSite = <T extends z.ZodType>(schema: T, kind: SiteKind): T => declareOn(schema, { soleSiteKind: kind })

/** Where a reference sits, as the trail of enclosing sites, outermost first. */
export type Site = { kind: SiteKind; id: string }
